// Generic menu + order demo template.
// Covers pizzeria, bakery, bar-pub, ice-cream, coffee-bar via ORDER_CONFIGS.
// Shows menu grid + cart + order mode (pickup / delivery / table).

import { useState } from 'react'
import DemoLayout from './shared/DemoLayout'
import type { ChatPersona } from './shared/ChatWidget'
import { useT } from './shared/i18n-ctx'
import type { Dict } from './shared/i18n-ctx'
import { ORDER_CONFIGS } from './configs'

const dict: Dict = {
  demo_tag: { en: 'Demo · Online ordering', mk: 'Демо · Онлајн нарачки', gr: 'Demo · Online παραγγελίες' },
  menu: { en: 'Menu', mk: 'Мени', gr: 'Μενού' },
  add: { en: 'Add', mk: 'Додај', gr: 'Προσθήκη' },
  cart: { en: 'Your cart', mk: 'Кошничка', gr: 'Καλάθι' },
  empty: { en: 'Cart is empty.', mk: 'Кошничката е празна.', gr: 'Το καλάθι είναι άδειο.' },
  total: { en: 'Total', mk: 'Вкупно', gr: 'Σύνολο' },
  checkout: { en: 'Checkout', mk: 'Нарачај', gr: 'Ολοκλήρωση' },
  ordered_pickup: { en: '✓ Order placed. Ready in 15 min for pickup.', mk: '✓ Нарачката е примена. Готова за 15 мин.', gr: '✓ Παραγγελία έτοιμη σε 15 λεπτά.' },
  ordered_delivery: { en: '✓ Order placed. Delivery in 30–45 min.', mk: '✓ Нарачката е примена. Достава за 30–45 мин.', gr: '✓ Παραγγελία σε 30–45 λεπτά.' },
  ordered_table: { en: '✓ Sent to the bar. Arriving at your table.', mk: '✓ Испратено до шанкот.', gr: '✓ Στάλθηκε στο μπαρ.' },
  features: { en: 'What\'s included', mk: 'Што вклучува', gr: 'Τι περιλαμβάνεται' },
  f1_pickup: { en: 'Mobile order ahead · skip the queue', mk: 'Нарачки преку телефон', gr: 'Παραγγελία από κινητό' },
  f1_delivery: { en: 'Your own delivery site · no Glovo / Wolt commission', mk: 'Сопствен сајт за достава', gr: 'Δικό σου site delivery' },
  f1_table: { en: 'QR at table · order without calling the waiter', mk: 'QR на маса · нарачај без да викаш', gr: 'QR στο τραπέζι' },
  f2: { en: 'Stripe + IBAN + cash payment', mk: 'Картичка + ИБАН + готовина', gr: 'Κάρτα + IBAN + μετρητά' },
  f3: { en: 'Multi-language menu (EN/MK/GR)', mk: 'Мени на 3 јазици', gr: 'Μενού σε 3 γλώσσες' },
  f4: { en: 'Built-in loyalty (every 10th free)', mk: 'Лојалност вградена', gr: 'Πρόγραμμα πιστότητας' },
  f5: { en: 'Daily specials promoted automatically', mk: 'Автоматски дневен специјалитет', gr: 'Καθημερινή προσφορά' },
  f6: { en: 'Customer data captured (emails, phones)', mk: 'Податоци за клиенти', gr: 'Δεδομένα πελατών' },
}

export default function OrderDemo({ slug }: { slug: string }) {
  const config = ORDER_CONFIGS[slug]
  if (!config) return <div className="p-10 text-center text-ink-100">No order config for &quot;{slug}&quot;.</div>
  const persona: ChatPersona = {
    name: config.industry.en,
    kind: 'order',
    items: config.items.map((i) => ({ name: i.name, price: i.price })),
  }
  return (
    <DemoLayout dict={dict} accent="from-orange-500 to-red-600" chatPersona={persona}>
      <Body slug={slug} />
    </DemoLayout>
  )
}

function Body({ slug }: { slug: string }) {
  const { t, lang } = useT()
  const config = ORDER_CONFIGS[slug]
  const [cart, setCart] = useState<Record<number, number>>({})
  const [ordered, setOrdered] = useState(false)

  const cartItems = Object.entries(cart).filter(([, n]) => n > 0)
  const total = cartItems.reduce((sum, [i, n]) => sum + config.items[+i].price * n, 0)

  const orderedKey = `ordered_${config.mode}` as const
  const f1Key = `f1_${config.mode}` as const

  return (
    <div className="bg-gradient-to-b from-ink-950 via-ink-950 to-ink-900">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <span className="chip bg-orange-500/10 text-orange-400 border border-orange-500/20 mb-5">
          {config.icon} {t('demo_tag')}
        </span>
        <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white">
          Demo · {config.industry[lang]}
        </h1>
        <p className="mt-4 text-lg text-ink-100 max-w-2xl">{config.tagline[lang]}</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 lg:grid-cols-[3fr_2fr] pb-12">
        <div>
          <h2 className="text-2xl font-bold text-white mb-4">{t('menu')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {config.items.map((item, i) => (
              <div key={i} className="card">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-white font-medium">{item.name}</h3>
                    {item.description && <p className="text-xs text-ink-500 mt-0.5">{item.description}</p>}
                    {item.tag && <span className="chip bg-ink-800 text-ink-100 border border-ink-800 text-[11px] mt-2">{item.tag}</span>}
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-white">€{item.price.toFixed(2)}</div>
                    <button
                      onClick={() => {
                        setOrdered(false)
                        setCart((c) => ({ ...c, [i]: (c[i] ?? 0) + 1 }))
                      }}
                      className="btn-secondary text-xs py-1.5 px-3 mt-2"
                    >
                      + {t('add')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="card lg:sticky lg:top-24 lg:self-start">
          <h2 className="text-lg font-semibold text-white mb-4">{t('cart')}</h2>
          {cartItems.length === 0 ? (
            <p className="text-ink-500 text-sm">{t('empty')}</p>
          ) : (
            <>
              <ul className="divide-y divide-ink-800">
                {cartItems.map(([i, n]) => (
                  <li key={i} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-ink-100">{config.items[+i].name} × {n}</span>
                    <span className="font-mono text-ink-100">€{(config.items[+i].price * n).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex items-center justify-between text-white font-semibold">
                <span>{t('total')}</span>
                <span>€{total.toFixed(2)}</span>
              </div>
              <button
                onClick={() => {
                  setOrdered(true)
                  setCart({})
                }}
                className="btn-primary w-full mt-4"
              >
                {t('checkout')}
              </button>
            </>
          )}
          {ordered && (
            <div className="mt-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 p-3 text-center text-sm font-medium">
              {t(orderedKey)}
            </div>
          )}
        </aside>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-2xl font-bold text-white mb-6">{t('features')}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {[f1Key, 'f2', 'f3', 'f4', 'f5', 'f6'].map((k) => (
            <div key={k} className="card flex gap-3 items-start">
              <span className="text-2xl">✓</span>
              <span className="text-ink-100">{t(k)}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
